import * as config from '@terraframing-suite/rx-config';
import { Box, Grid, MenuItem, Tooltip } from '@mui/material';
import { range } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import appConfig from '../../../../config';
import { Select, NumericInput } from './form-controls';
import { Section } from './Section';
import { useLpkRunContext } from './lpk-run-context';
import { LimeCalculator } from './LimeCalculator';
import { Checkbox } from '../../../run-rx/form-controls';
import { InputsHeader } from '../../../run-rx/InputsHeader';

const stateOptions = Object.entries(config.lime.states).map(
  ([id, { label }]) => (
    <MenuItem key={id} value={id}>
      {label}
    </MenuItem>
  ),
);

const tillageOptions = appConfig.tillageTypes.map(({ id, label }) => (
  <MenuItem key={id} value={id}>
    {label}
  </MenuItem>
));

const plannedMonthOptions = range(1, 13).map((month) => (
  <MenuItem key={month} value={month}>
    {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
  </MenuItem>
));

const currentYear = new Date().getFullYear();
const plannedYearOptions = range(currentYear, currentYear + 12).map((year) => (
  <MenuItem key={year} value={year}>
    {year}
  </MenuItem>
));

const sourceOptions = config.lime.sources.map(({ id, label }) => (
  <MenuItem key={id} value={id}>
    {label}
  </MenuItem>
));

export function LimeSection() {
  const { selectedNutrients, getParam, updateParam } = useLpkRunContext();
  const [isCalculating, setCalculating] = useState(false);

  const stateConfig = useMemo(
    () => config.lime.states[getParam('lime.state')],
    [getParam],
  );

  const sourceConfig = useMemo(
    () =>
      config.lime.sources.find(
        (source) => source.id === getParam('lime.source'),
      ),
    [getParam],
  );

  useEffect(() => {
    if (stateConfig)
      updateParam('lime.referenceQuality', stateConfig?.referenceQuality);
  }, [stateConfig, updateParam]);

  return (
    selectedNutrients.lime && (
      <Section>
        <InputsHeader title="L Products" sx={{ mb: 0 }} />

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={6}>
            <Select
              required
              label="Application State"
              name="lime.state"
              options={stateOptions}
            />
            <Select
              required
              label="Tillage Type Following L App"
              name="lime.tillageType"
              options={tillageOptions}
            />
            <NumericInput
              required
              min={0}
              ignoreNonNumericInput
              name="lime.quality"
              label="RNV"
              disabled={isCalculating}
            />
            <Select
              required
              label="Planned App Month"
              name="lime.applicationMonth"
              options={plannedMonthOptions}
            />
          </Grid>
          <Grid item xs={6}>
            <NumericInput
              required
              disabled
              name="lime.referenceQuality"
              label="Ref. Lime Quality"
            />
            <Select
              required
              label="L Source"
              name="lime.source"
              options={sourceOptions}
            />
            <Grid item sx={{ pt: 3 }}>
              <Box sx={{ height: 40 }} />
            </Grid>
            <Select
              required
              label="Planned App Year"
              name="lime.applicationYear"
              options={plannedYearOptions}
            />
          </Grid>
          {buildCalculator()}
        </Grid>
      </Section>
    )
  );

  function buildCalculator() {
    function updateCalculated(results) {
      updateParam('lime.quality', results.quality);
      updateParam('lime.qualityVariables', results.variables);
    }

    const canCalculate = stateConfig && sourceConfig;
    const checkbox = (
      <Checkbox
        label="UNKNOWN RNV"
        checked={isCalculating}
        disabled={!canCalculate}
        sx={{ width: 'auto' }}
        onChange={(e) => {
          setCalculating(e.target.checked);
          updateCalculated({ quality: '' });
        }}
      />
    );
    const button = canCalculate ? (
      checkbox
    ) : (
      <Tooltip title="Select state and source first">{checkbox}</Tooltip>
    );

    return (
      <>
        <Grid item xs={12}>
          {button}
        </Grid>
        {isCalculating && (
          <LimeCalculator
            state={getParam('lime.state')}
            passThrough={sourceConfig?.qualityPassThrough}
            onChange={updateCalculated}
          />
        )}
      </>
    );
  }
}
